import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import ModalConfirmLogout from "../../../components/modals/ModalConfirmLogout";
import useAuth from "../../../hooks/useAuth";
import useCompany from "../../../hooks/useCompany";
import { useStoreTheme } from "../../../store/storeTheme";
import {
    Home, Users, WalletCards, ShoppingCart, Calendar,
    User, Settings, Component, Coins, Workflow, LogOut, BookOpen,
    PanelLeftClose, PanelLeftOpen, ShieldCheck,
} from "lucide-react";

function SidebarItem({ item, collapsed }) {
    const location = useLocation();
    const [tooltipPos, setTooltipPos] = useState(null);
    const itemRef = useRef(null);

    if (item.type === "divider") {
        return <div className="h-[0.5px] bg-gray-300 my-1 w-full" />;
    }

    if (!item.icon) {
        if (collapsed) return null;
        return <div className="mt-4 px-4 font-semibold text-gray-600">{item.name}</div>;
    }

    const isActive = item.path === location.pathname ||
        (item.path !== '/dashboard/home' && location.pathname.startsWith(item.path));

    const showTooltip = () => {
        if (!collapsed || !itemRef.current) return;
        const rect = itemRef.current.getBoundingClientRect();
        setTooltipPos({ top: rect.top + rect.height / 2, left: rect.right + 12 });
    };

    const hideTooltip = () => setTooltipPos(null);

    return (
        <div ref={itemRef} onMouseEnter={showTooltip} onMouseLeave={hideTooltip}>
            <Link
                to={item.path || "#"}
                className={[
                    "flex items-center rounded-xl transition",
                    collapsed
                        ? "justify-center w-12 h-12 mx-auto"
                        : "2xl:h-12 h-10 2xl:space-x-4 space-x-3 2xl:px-4 px-3 w-full",
                    "hover:bg-white",
                    isActive ? "bg-white" : "",
                ].join(" ")}
            >
                <item.icon className="2xl:w-6 2xl:h-6 w-5 h-5 shrink-0" />
                {!collapsed && (
                    <span className="font-semibold truncate whitespace-nowrap 2xl:text-base text-sm">{item.name}</span>
                )}
            </Link>

            {tooltipPos && (
                <div
                    className="fixed z-[9999] pointer-events-none -translate-y-1/2"
                    style={{ top: tooltipPos.top, left: tooltipPos.left }}
                >
                    <div className="bg-gray-900 text-white text-xs font-medium px-3 py-1.5 rounded-lg shadow-lg whitespace-nowrap relative">
                        {item.name}
                        <div className="absolute right-full top-1/2 -translate-y-1/2 w-0 h-0 border-t-[5px] border-t-transparent border-b-[5px] border-b-transparent border-r-[5px] border-r-gray-900" />
                    </div>
                </div>
            )}
        </div>
    );
}

export default function Sidebar() {
    const { user, logout } = useAuth();
    const { activeCompany } = useCompany();
    const { sidebarCollapsed, toggleSidebar } = useStoreTheme();
    const [heightPerColor, setHeightPerColor] = useState(0);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [logoutTooltipPos, setLogoutTooltipPos] = useState(null);
    const logoutRef = useRef(null);
    const colors = ["#2461DD", "#000000", "#A1A1A1", "#6D9CE2", "#2B36D9", "#A1A1A1"];

    const modules = activeCompany?.modules || [];
    const isEventsModuleEnabled = modules.some((module) =>
        typeof module === 'string' ? module === 'event_module' : module.slug === 'event_module'
    );
    const isRwaModuleEnabled = modules.some((module) =>
        typeof module === 'string' ? module === 'rwa_module' : module.slug === 'rwa_module'
    );
    const isAPIKeyModuleEnabled = modules.some((module) =>
        typeof module === 'string' ? module === 'api_key_module' : module.slug === 'api_key_module'
    );
    const isSSOModuleEnabled = modules.some((module) =>
        typeof module === 'string' ? module === 'sso_module' : module.slug === 'sso_module'
    );

    const mainMenu = [
        { name: "Home", icon: Home, path: "/dashboard/home", subItems: [] },
        { name: "Design", icon: Component, path: "/dashboard/design", subItems: [] },
        { name: "Configuration", icon: Settings, path: "/dashboard/wallet-configuration", subItems: [] },
        { name: "Tokens", icon: Coins, path: "/dashboard/tokens", subItems: [] },
        { name: "Members", icon: User, path: "/dashboard/members", subItems: [] },
        //{ name: "URL Builder", icon: Calendar, path: "/dashboard/url-builder", subItems: [] },
        user.role === 'admin' && { name: "Workflows", icon: Workflow, path: "/dashboard/workflows", subItems: [] },
        { type: "divider" },
        isEventsModuleEnabled && { name: "Events", icon: Calendar, path: "/dashboard/events", subItems: [] },
        isRwaModuleEnabled && { name: "Assets", icon: ShoppingCart, path: "/dashboard/rwa", subItems: [] },
        isAPIKeyModuleEnabled && { name: "API Keys", icon: WalletCards, path: "/dashboard/apikeys", subItems: [] },
        isSSOModuleEnabled && { name: "SSO Providers", icon: ShieldCheck, path: "/dashboard/sso", subItems: [] },
        { name: "Admin Users", icon: Users, path: "/dashboard/admin-users", subItems: [] },
    ].filter(Boolean);

    useEffect(() => {
        const handleResize = () => {
            setHeightPerColor(window.innerHeight / colors.length);
        };
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [colors.length]);

    const confirmLogout = () => {
        logout();
    };

    return (
        <div
            className={[
                "bg-[#F2F2F2] py-6 relative overflow-hidden flex flex-col transition-all duration-300 shrink-0",
                sidebarCollapsed ? "w-[72px] pl-4 pr-2" : "2xl:w-[340px] xl:w-[280px] w-[240px] 2xl:px-6 px-4",
            ].join(" ")}
        >
            {/* Color bar */}
            <div className="absolute top-0 left-0 h-full w-2">
                {colors.map((color, index) => (
                    <div key={index} style={{ height: heightPerColor, backgroundColor: color }} />
                ))}
            </div>

            {/* Logo + Collapse toggle */}
            <div className={[
                "flex items-center mb-4 min-h-[65px]",
                sidebarCollapsed ? "justify-center" : "justify-between",
            ].join(" ")}>
                {!sidebarCollapsed && (
                    <div className="flex items-center justify-center">
                        <img src="/logo/engage.png" alt="Logo" className="w-full h-full object-contain" />
                    </div>
                )}
                <button
                    onClick={toggleSidebar}
                    className="p-2 rounded-lg hover:bg-white transition shrink-0"
                    title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                >
                    {sidebarCollapsed ? (
                        <PanelLeftOpen className="w-5 h-5 text-gray-500" />
                    ) : (
                        <PanelLeftClose className="w-5 h-5 text-gray-500" />
                    )}
                </button>
            </div>

            {/* Menu */}
            <div className="grid content-start overflow-y-auto flex-1 gap-2">
                {mainMenu.map((item, index) => (
                    <SidebarItem
                        key={item.name || `divider-${index}`}
                        item={item}
                        collapsed={sidebarCollapsed}
                    />
                ))}
            </div>

            {/* Docs + Logout */}
            <div className="mt-auto space-y-2" ref={logoutRef}>
                <a
                    href="https://onchainlabs-tech-documentation.gitbook.io/wallettwo-documentation"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={[
                        "flex items-center rounded-xl hover:bg-white cursor-pointer transition",
                        sidebarCollapsed
                            ? "justify-center w-10 h-10 mx-auto"
                            : "h-10 space-x-3 px-4 w-full",
                    ].join(" ")}
                >
                    <BookOpen className="w-5 h-5 shrink-0" />
                    {!sidebarCollapsed && <span>Documentation</span>}
                </a>
                <button
                    onClick={() => setIsModalOpen(true)}
                    onMouseEnter={() => {
                        if (!sidebarCollapsed || !logoutRef.current) return;
                        const rect = logoutRef.current.getBoundingClientRect();
                        setLogoutTooltipPos({ top: rect.top + rect.height / 2, left: rect.right + 12 });
                    }}
                    onMouseLeave={() => setLogoutTooltipPos(null)}
                    className={[
                        "flex items-center rounded-xl bg-gray-100 hover:bg-white cursor-pointer transition",
                        sidebarCollapsed
                            ? "justify-center w-10 h-10 mx-auto"
                            : "h-10 space-x-3 px-4 w-full",
                    ].join(" ")}
                >
                    <LogOut className="w-5 h-5 shrink-0" />
                    {!sidebarCollapsed && <span>Log out</span>}
                </button>

                {logoutTooltipPos && (
                    <div
                        className="fixed z-[9999] pointer-events-none -translate-y-1/2"
                        style={{ top: logoutTooltipPos.top, left: logoutTooltipPos.left }}
                    >
                        <div className="bg-gray-900 text-white text-xs font-medium px-3 py-1.5 rounded-lg shadow-lg whitespace-nowrap relative">
                            Log out
                            <div className="absolute right-full top-1/2 -translate-y-1/2 w-0 h-0 border-t-[5px] border-t-transparent border-b-[5px] border-b-transparent border-r-[5px] border-r-gray-900" />
                        </div>
                    </div>
                )}
            </div>

            <ModalConfirmLogout
                isOpen={isModalOpen}
                closeModal={() => setIsModalOpen(false)}
                onConfirm={confirmLogout}
            />
        </div>
    );
}
